import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { COMPANY, CAPABILITIES } from "../data/site";
import { Phone, Mail, MapPin, Send, Building2 } from "lucide-react";
import { toast } from "sonner";

const initialState = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  serviceNeed: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message fields.");
      return;
    }
    const subject = encodeURIComponent(
      `Inquiry from ${form.name}${form.organization ? ` (${form.organization})` : ""}`
    );
    const lines = [
      `Name: ${form.name}`,
      form.organization ? `Organization: ${form.organization}` : null,
      `Email: ${form.email}`,
      form.phone ? `Phone: ${form.phone}` : null,
      form.serviceNeed ? `Service Need: ${form.serviceNeed}` : null,
      "",
      "Message:",
      form.message,
    ].filter(Boolean);
    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
    toast.success("Opening your email client…");
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        eyebrow="Contact Street Society"
        title="Let’s discuss your scope, set-aside, or teaming opportunity."
        intro="Reach out to discuss capability statements, upcoming procurement, recurring service needs, or partnership opportunities. We respond promptly to inquiries from contracting officers, primes, and commercial clients."
        image="govBuilding"
      />

      <section className="section-y bg-white" data-testid="contact-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <p className="text-overline text-[#4B7055] mb-3">Send a Message</p>
            <h2 className="heading-section text-3xl sm:text-4xl text-[#0A1F35]">
              Tell us about your service need.
            </h2>
            <p className="mt-4 text-[#4A5568]">
              Complete the form and we’ll route your inquiry directly to our team via email.
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
              data-testid="contact-form"
            >
              <Field
                label="Name"
                required
                value={form.name}
                onChange={update("name")}
                testid="contact-name"
                placeholder="Full name"
              />
              <Field
                label="Organization"
                value={form.organization}
                onChange={update("organization")}
                testid="contact-organization"
                placeholder="Agency / Company"
              />
              <Field
                label="Email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                testid="contact-email"
                placeholder="you@example.gov"
              />
              <Field
                label="Phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                testid="contact-phone"
                placeholder="(347) 555-0100"
              />
              <div className="sm:col-span-2">
                <label className="text-overline text-[#4A5568] mb-2 block">Service Need</label>
                <select
                  value={form.serviceNeed}
                  onChange={update("serviceNeed")}
                  className="w-full bg-white border border-[#E2E8F0] px-4 py-3.5 text-sm text-[#1A2530] focus:outline-none focus:border-[#0A1F35] focus-ring transition-colors"
                  data-testid="contact-service-need"
                >
                  <option value="">Select a capability area (optional)</option>
                  {CAPABILITIES.map((c) => (
                    <option key={c.id} value={c.title}>
                      {c.title}
                    </option>
                  ))}
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-overline text-[#4A5568] mb-2 block">Message *</label>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Briefly describe your scope, timing, or question."
                  className="w-full bg-white border border-[#E2E8F0] px-4 py-3.5 text-sm text-[#1A2530] focus:outline-none focus:border-[#0A1F35] focus-ring transition-colors resize-y"
                  data-testid="contact-message"
                  required
                />
              </div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between pt-3">
                <p className="text-xs text-[#4A5568]">
                  Submitting opens your default email client with your message pre-filled to
                  <span className="text-[#0A1F35] font-medium"> {COMPANY.email}</span>.
                </p>
                <button type="submit" className="btn-base btn-primary" data-testid="contact-submit">
                  Send Inquiry <Send size={16} />
                </button>
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[#E2E8F0]" data-testid="contact-info">
            <p className="text-overline text-[#4B7055] mb-3">Direct Contact</p>
            <h3 className="font-serif text-2xl text-[#0A1F35] font-bold">
              Street Society LLC
            </h3>

            <ul className="mt-8 space-y-6">
              <ContactRow
                icon={Phone}
                label="Phone"
                value={COMPANY.phone}
                href={COMPANY.phoneHref}
                testid="contact-info-phone"
              />
              <ContactRow
                icon={Mail}
                label="Email"
                value={COMPANY.email}
                href={COMPANY.emailHref}
                testid="contact-info-email"
              />
              <ContactRow
                icon={MapPin}
                label="Location"
                value={COMPANY.location}
                testid="contact-info-location"
              />
              <ContactRow
                icon={Building2}
                label="Entity"
                value={`UEI ${COMPANY.uei} · CAGE ${COMPANY.cage}`}
                testid="contact-info-entity"
              />
            </ul>

            <div className="mt-10 border-t border-[#E2E8F0] pt-8">
              <p className="text-overline text-[#4B7055] mb-3">Response Window</p>
              <p className="text-sm text-[#4A5568] leading-relaxed">
                We respond to inquiries from contracting officers, prime contractors, and commercial
                clients on the next business day or sooner.
              </p>
            </div>

            <div className="mt-10 border border-[#E2E8F0] p-6 bg-[#F5F7F9]">
              <p className="text-overline text-[#4B7055] mb-2">Capability Statement</p>
              <p className="text-sm text-[#4A5568] mb-4">
                Download the latest one-page capability statement.
              </p>
              <a
                href="/government-contracting#capability-statement-section"
                className="btn-base btn-outline-dark w-full justify-center"
                data-testid="sidebar-capability-link"
              >
                Get the PDF
              </a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-overline text-[#4A5568] mb-2 block">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white border border-[#E2E8F0] px-4 py-3.5 text-sm text-[#1A2530] placeholder:text-[#9CA5B0] focus:outline-none focus:border-[#0A1F35] focus-ring transition-colors"
      />
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const content = (
    <>
      <span className="inline-flex h-11 w-11 items-center justify-center bg-[#0A1F35] text-white shrink-0">
        <Icon size={18} strokeWidth={1.75} />
      </span>
      <div>
        <p className="text-overline text-[#4A5568]">{label}</p>
        <p className="font-serif text-base text-[#0A1F35] font-bold mt-0.5 break-words">{value}</p>
      </div>
    </>
  );
  if (href) {
    return (
      <li>
        <a
          href={href}
          className="flex items-start gap-4 hover:text-[#4B7055] transition-colors"
          data-testid={testid}
        >
          {content}
        </a>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-4" data-testid={testid}>
      {content}
    </li>
  );
}
